import type { CmCluster, CmClusterNode } from '@bloo-team/api-client'

/**
 * Vantage · Cases · a case's cluster (docs/cases.md §Case page · Clusters) —
 * pure helpers over `GET /cases/{id}/cluster`: which link ties each case to
 * the one a hop nearer the start, the side list's groups, and the radial
 * layout the graph draws (the start in the middle, one ring per hop).
 */

export interface ClusterParent {
  /** The case one hop nearer the start. */
  parent: string
  /** Shared entities on that link. */
  shared: number
}

/**
 * For every case but the start: its strongest link to a case one hop nearer
 * (most shared entities; ties to the lower case id).
 */
export function clusterParents(g: CmCluster): Map<string, ClusterParent> {
  const dist = new Map(g.nodes.map((n) => [n.id, n.distance]))
  const out = new Map<string, ClusterParent>()
  for (const e of g.edges) {
    for (const [child, parent] of [
      [e.a, e.b],
      [e.b, e.a],
    ] as const) {
      const dc = dist.get(child)
      const dp = dist.get(parent)
      if (dc === undefined || dp === undefined || dp !== dc - 1) continue
      const best = out.get(child)
      if (!best || e.shared > best.shared || (e.shared === best.shared && parent < best.parent)) out.set(child, { parent, shared: e.shared })
    }
  }
  return out
}

export interface ClusterGroup {
  distance: number
  label: string
  nodes: CmClusterNode[]
}

/** The side list: *This case*, then *1 hop away*, *2 hops away*… — in the service's order (id within a hop). */
export function clusterGroups(g: CmCluster): ClusterGroup[] {
  const byHop = new Map<number, CmClusterNode[]>()
  for (const n of g.nodes) byHop.set(n.distance, [...(byHop.get(n.distance) ?? []), n])
  return [...byHop]
    .sort(([a], [b]) => a - b)
    .map(([d, nodes]) => ({ distance: d, label: d === 0 ? 'This case' : `${d} hop${d === 1 ? '' : 's'} away`, nodes }))
}

export interface ClusterPoint {
  x: number
  y: number
}

/** Margin left for the outer ring's labels, in layout units. */
const EDGE_ROOM = 130
/** About one clipped title, in layout units (the graph draws them 30 characters long). */
const LABEL_WIDTH = 250

/**
 * The start in the middle, one ring per hop. The first ring is spread evenly
 * from 12 o'clock in id order. On an outer ring each case aims at its
 * parent's angle — siblings side by side around it, one slot (a title's
 * width) apart — and a case that would crowd its neighbour moves on by a
 * slot. Deterministic for one cluster.
 */
export function clusterLayout(g: CmCluster, size = 1000): Map<string, ClusterPoint> {
  const out = new Map<string, ClusterPoint>()
  if (!g.nodes.length) return out
  const c = size / 2
  const maxHop = Math.max(...g.nodes.map((n) => n.distance))
  const gap = maxHop ? (size / 2 - EDGE_ROOM) / maxHop : 0
  const parents = clusterParents(g)
  const angle = new Map<string, number>()
  const place = (id: string, r: number, a: number) => {
    angle.set(id, a)
    out.set(id, { x: Math.round(c + r * Math.cos(a)), y: Math.round(c + r * Math.sin(a)) })
  }
  for (let d = 0; d <= maxHop; d++) {
    const ring = g.nodes.filter((n) => n.distance === d).map((n) => n.id)
    if (!ring.length) continue
    if (d === 0) {
      place(ring[0]!, 0, -Math.PI / 2)
      continue
    }
    if (d === 1) {
      const first = [...ring].sort()
      first.forEach((id, i) => place(id, gap, (2 * Math.PI * i) / first.length - Math.PI / 2))
      continue
    }
    // A slot is one title wide on this ring — or less, when the ring holds more cases than that allows.
    const step = Math.min((2 * Math.PI) / ring.length, LABEL_WIDTH / (d * gap))
    // Siblings around their parent's angle, in id order.
    const families = new Map<string, string[]>()
    for (const id of [...ring].sort()) {
      const p = parents.get(id)?.parent ?? ''
      families.set(p, [...(families.get(p) ?? []), id])
    }
    const aims: [string, number][] = []
    for (const [p, kids] of families) {
      const base = angle.get(p) ?? -Math.PI / 2
      kids.forEach((id, i) => aims.push([id, base + (i - (kids.length - 1) / 2) * step]))
    }
    aims.sort((x, y) => norm(x[1]) - norm(y[1]) || x[0].localeCompare(y[0]))
    // One slot apart at least; if the ring cannot hold that, an even spread.
    const placed: [string, number][] = []
    for (const [id, a] of aims) {
      const prev = placed[placed.length - 1]
      const t = prev ? Math.max(norm(a), prev[1] + step) : norm(a)
      placed.push([id, t])
    }
    const span = placed.length > 1 ? placed[placed.length - 1]![1] - placed[0]![1] : 0
    if (span > 2 * Math.PI - step + 1e-9) placed.forEach((x, i) => (x[1] = placed[0]![1] + (2 * Math.PI * i) / placed.length))
    for (const [id, a] of placed) place(id, d * gap, a)
  }
  return out
}

const norm = (a: number) => ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)
